import { appStore } from './store';
import { authStore } from './authStore';
import { flushPendingPersonalNotes, loadPersonalMarkerNotes, queuePersonalMarkerNote } from './personalNotesSync';
import { addSyncDiagnostic } from './syncDiagnostics';
import type { MarkerNoteFormat, MarkerSheetData, SongRecord } from './types';

const TEXT_PREFIX = 'liveMediaPlayer.performance.myNotes';
const FORMAT_PREFIX = 'liveMediaPlayer.performance.myNotesFormat';
const SHEET_PREFIX = 'liveMediaPlayer.performance.myNotesSheet';
const SNAPSHOT_PREFIX = 'maximumBand.personalNotes.lastSyncedSnapshot.v1';
const MY_NOTES_TAB_ID = 'my-notes';

interface NoteSnapshot {
  content: string;
  format: MarkerNoteFormat;
  sheetData: MarkerSheetData;
}

export type PersonalMyNoteQueueResult =
  | { queued: true }
  | { queued: false; reason: 'not-signed-in' | 'song-not-synced' };

function storageKey(prefix: string, songId: string, markerId: string): string {
  return `${prefix}.${songId}.${markerId}`;
}

function readString(key: string): string {
  try { return localStorage.getItem(key) ?? ''; } catch { return ''; }
}

function writeString(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* Existing local note behavior remains best-effort. */ }
}

function localFormat(songId: string, markerId: string): MarkerNoteFormat {
  return readString(storageKey(FORMAT_PREFIX, songId, markerId)) === 'sheet' ? 'sheet' : 'text';
}

function localSheet(songId: string, markerId: string): MarkerSheetData {
  try {
    const raw = localStorage.getItem(storageKey(SHEET_PREFIX, songId, markerId));
    if (!raw) return { rows: [] };
    const parsed = JSON.parse(raw) as MarkerSheetData;
    return parsed && Array.isArray(parsed.rows) ? parsed : { rows: [] };
  } catch {
    return { rows: [] };
  }
}

function localSnapshot(songId: string, markerId: string): NoteSnapshot {
  return {
    content: readString(storageKey(TEXT_PREFIX, songId, markerId)),
    format: localFormat(songId, markerId),
    sheetData: localSheet(songId, markerId),
  };
}

function snapshotKey(songId: string, markerId: string): string {
  return storageKey(SNAPSHOT_PREFIX, songId, markerId);
}

function readLastSyncedSnapshot(songId: string, markerId: string): NoteSnapshot | null {
  try {
    const raw = localStorage.getItem(snapshotKey(songId, markerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NoteSnapshot;
    if (!parsed || typeof parsed.content !== 'string' || (parsed.format !== 'text' && parsed.format !== 'sheet') || !Array.isArray(parsed.sheetData?.rows)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLastSyncedSnapshot(songId: string, markerId: string, value: NoteSnapshot): void {
  writeString(snapshotKey(songId, markerId), JSON.stringify(value));
}

function sameSnapshot(a: NoteSnapshot | null, b: NoteSnapshot): boolean {
  return !!a && JSON.stringify(a) === JSON.stringify(b);
}

function hasValue(value: NoteSnapshot): boolean {
  return value.content.trim().length > 0 || value.sheetData.rows.some((row) => row.some((cell) => cell.text.trim().length > 0));
}

function cloudSongId(song: SongRecord): string | null {
  return song.supabaseSongId;
}

function queueSnapshot(song: SongRecord, markerId: string, value: NoteSnapshot): void {
  const songSupabaseId = cloudSongId(song);
  if (!songSupabaseId || !authStore.getSession()) return;
  queuePersonalMarkerNote({
    songSupabaseId,
    markerClientId: markerId,
    tabId: MY_NOTES_TAB_ID,
    content: value.content,
    format: value.format,
    sheetData: value.sheetData,
  });
}

export function queuePersonalMyNoteForSync(song: SongRecord, markerId: string): PersonalMyNoteQueueResult {
  if (!cloudSongId(song)) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-save',
      message: 'Saved locally but did not queue cloud sync because this song has no Supabase song id.',
      details: { localSongId: song.id, songName: song.name, markerClientId: markerId },
    });
    return { queued: false, reason: 'song-not-synced' };
  }
  if (!authStore.getSession()) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-save',
      message: 'Saved locally but did not queue cloud sync because no auth session is active.',
      details: { localSongId: song.id, songName: song.name, songSupabaseId: cloudSongId(song), markerClientId: markerId },
    });
    return { queued: false, reason: 'not-signed-in' };
  }
  queueSnapshot(song, markerId, localSnapshot(song.id, markerId));
  void flushPendingPersonalNotes();
  return { queued: true };
}

async function syncSong(song: SongRecord): Promise<void> {
  const songSupabaseId = cloudSongId(song);
  if (!songSupabaseId) return;
  const result = await loadPersonalMarkerNotes(songSupabaseId);
  if (result.error) {
    addSyncDiagnostic({
      level: 'error',
      source: 'my-notes-match',
      message: 'Skipped matching My Notes because the cloud pull failed.',
      details: { localSongId: song.id, songName: song.name, songSupabaseId, error: result.error },
    });
    return;
  }

  const cloudByMarker = new Map(result.notes.filter((note) => note.tabId === MY_NOTES_TAB_ID).map((note) => [note.markerClientId, note]));
  let matchedMarkers = 0;
  let wroteLocalMarkers = 0;
  let queuedLocalChanges = 0;
  const matchedMarkerSummaries: string[] = [];
  for (const marker of song.markers) {
    const local = localSnapshot(song.id, marker.id);
    const lastSynced = readLastSyncedSnapshot(song.id, marker.id);
    const localChangedSinceSync = lastSynced !== null && !sameSnapshot(lastSynced, local);
    const cloud = cloudByMarker.get(marker.id);

    if (localChangedSinceSync) {
      // Local-first rule: once this device has a known synchronized baseline,
      // any divergence from it is an intentional local edit. Queue it before
      // considering a cloud pull so another device cannot erase unsent work.
      queueSnapshot(song, marker.id, local);
      queuedLocalChanges += 1;
      continue;
    }

    if (cloud) {
      matchedMarkers += 1;
      const cloudSnapshot: NoteSnapshot = {
        content: cloud.content,
        format: cloud.format,
        sheetData: cloud.sheetData,
      };
      const alreadySame = sameSnapshot(local, cloudSnapshot);
      matchedMarkerSummaries.push(`${marker.label}:${marker.id}:contentLength=${cloud.content.length}:alreadySame=${alreadySame}`);
      if (!alreadySame) {
        writeString(storageKey(TEXT_PREFIX, song.id, marker.id), cloudSnapshot.content);
        writeString(storageKey(FORMAT_PREFIX, song.id, marker.id), cloudSnapshot.format);
        writeString(storageKey(SHEET_PREFIX, song.id, marker.id), JSON.stringify(cloudSnapshot.sheetData));
        wroteLocalMarkers += 1;
      }
      writeLastSyncedSnapshot(song.id, marker.id, cloudSnapshot);
      continue;
    }

    // Migration rule for existing installs: if this device already has My
    // Notes but the cloud has never seen them, preserve and upload them.
    if (hasValue(local)) {
      queueSnapshot(song, marker.id, local);
      queuedLocalChanges += 1;
    }
  }
  addSyncDiagnostic({
    level: 'info',
    source: 'my-notes-match',
    message: 'Matched cloud My Notes against local song markers.',
    details: {
      localSongId: song.id,
      songName: song.name,
      songSupabaseId,
      localMarkerCount: song.markers.length,
      cloudMyNotesRows: cloudByMarker.size,
      matchedMarkers,
      wroteLocalMarkers,
      queuedLocalChanges,
      localMarkerClientIds: song.markers.map((marker) => marker.id).join(', '),
      cloudMarkerClientIds: [...cloudByMarker.keys()].join(', '),
      matchedMarkerSummaries: matchedMarkerSummaries.join(', '),
    },
  });
}

export async function syncPersonalMyNotes(): Promise<void> {
  if (!authStore.getSession()) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-sync',
      message: 'Skipped My Notes sync because no auth session is active.',
      details: {},
    });
    return;
  }
  await flushPendingPersonalNotes();
  const songs = appStore.getState().songs.filter((song) => song.supabaseSongId);
  addSyncDiagnostic({
    level: 'info',
    source: 'my-notes-sync',
    message: 'Starting My Notes sync for local cloud-linked songs.',
    details: {
      linkedSongCount: songs.length,
      linkedSongSupabaseIds: songs.map((song) => song.supabaseSongId).filter(Boolean).join(', '),
      linkedLocalSongIds: songs.map((song) => song.id).join(', '),
    },
  });
  for (const song of songs) await syncSong(song);
  await flushPendingPersonalNotes();

  // A successful flush establishes the baseline used to recognize future
  // local edits without touching the large Performance player file. Read the
  // just-written cloud state once more and record only values that actually
  // match local storage, so failed/offline writes never masquerade as synced.
  for (const song of songs) {
    const songSupabaseId = cloudSongId(song);
    if (!songSupabaseId) continue;
    const result = await loadPersonalMarkerNotes(songSupabaseId);
    if (result.error) continue;
    const cloudByMarker = new Map(result.notes.filter((note) => note.tabId === MY_NOTES_TAB_ID).map((note) => [note.markerClientId, note]));
    for (const marker of song.markers) {
      const cloud = cloudByMarker.get(marker.id);
      if (!cloud) continue;
      const cloudSnapshot: NoteSnapshot = { content: cloud.content, format: cloud.format, sheetData: cloud.sheetData };
      if (sameSnapshot(localSnapshot(song.id, marker.id), cloudSnapshot)) writeLastSyncedSnapshot(song.id, marker.id, cloudSnapshot);
    }
  }
}
